<template>
  <div id="partsStoreOrderDetails">
    <div
      v-if="!loadingOrderData && (!userIsOrderAdmin || !hasAccessToFulfillmentAccount)"
      class="container"
    >
      <b-alert show variant="danger">
        {{ getContentItem("niceNoAccessToOrder") }}
      </b-alert>
    </div>
    <div v-else class="container">
      <b-row>
        <b-col xs="12" lg="10">
          <h1>{{ getContentItem("partsStoreOrderHashSign") }} {{ partsStoreOrderDetailsDto.OrderNumber }}</h1>
        </b-col>
        <b-col xs="12" lg="2">
          <a class="pull-right mt-2 noprint" href="#" @click="printOrder()">
            <span>
              <i class="icon-printer" />
            </span>
            {{ getContentItem("print") }}
          </a>
        </b-col>
      </b-row>

      <b-row v-if="isOpen" class="noprint">
        <b-col xs="12">
          <br>
          <p>{{ getContentItem("indicateFulfillmentPrecerences") }}</p>
        </b-col>
      </b-row>
      <b-row
        v-if="isClosed && partsStoreOrderDetailsDto.DealerNetOrders && partsStoreOrderDetailsDto.DealerNetOrders.length > 0"
      >
        <b-col xs="12">
          <b-alert show variant="info">
            <h5>{{ getContentItem("dealerNetOrders") }}</h5>
            <br>
            <span
              v-for="(dnOrder, index) in partsStoreOrderDetailsDto.DealerNetOrders"
              :key="index"
            >
              <order-details-link
                :order-number="dnOrder.DealerNetOrderNumber"
                :order-source ="dnOrder.DealerNetOrderSource"
                :dealer-code="dnOrder.DealerCode"
                :sales-organization="dnOrder.SalesOrganization"
              />
              <br>
            </span>
          </b-alert>
        </b-col>
      </b-row>
      <b-row v-if="isSunbeltOrder && !IsMcfe">
        <b-col xs="12">
          <b-alert
            id="sunbeltMessage"
            show
            variant="danger"
          >
            {{ getContentItem("reminderSunbeltInvoices") }}
          </b-alert>
        </b-col>
      </b-row>
      <b-row v-if="showThirdPartyShippingWarning">
        <b-col xs="12">
          <b-alert id="thirdPartyShippingWarning" show variant="danger">
            {{ format(getContentItem("ThirdPartyBillingAccountCheck"), partsStoreOrderDetailsDto.ThirdPartyBillingAccountNumber) }}
          </b-alert>
        </b-col>
      </b-row>
      <h2>{{ getContentItem("orderInformation") }}</h2>
      <b-row>
        <b-col xs="12">
          <b-table
            id="orderInfoTable"
            striped
            hover
            stacked="md"
            class="blue"
            show-empty
            :fields="orderInfoColumns"
            :items="orderInfoRow"
            :no-provider-filtering="true"
            :no-local-sorting="true"
          >
            <template
              v-if="!loadingOrderData"
              slot="Customer"
            >
              {{ partsStoreOrderDetailsDto.Customer.Name }} ({{ partsStoreOrderDetailsDto.Customer.CustomerNumber }})
            </template>
            <template
              v-if="!loadingOrderData"
              slot="CustomerPONumber"
            >
              {{ partsStoreOrderDetailsDto.PoNumber }}
            </template>
            <template slot="ShipVia">
              <b-form-group v-show="isOpen && showEditing.ShippingMethod">
                <b-form-select
                  id="shipVia"
                  v-model="editObject.ShippingMethod"
                  class="disableClear"
                  :options="shippingOptions"
                />
                <span
                  v-show="showEditing.ShippingMethod"
                  @click="save(); toggleShowEditing('ShippingMethod', true);"
                >
                  <i
                    v-show="isOpen"
                    class="icon-checkmark icon-medium mt-1 mr-1"
                  />
                </span>
                <span
                  v-show="showEditing.ShippingMethod"
                  @click="toggleShowEditing('ShippingMethod')"
                >
                  <i v-show="isOpen" class="icon-x icon-medium mt-1" />
                </span>
              </b-form-group>
              <span
                v-if="!showEditing.ShippingMethod"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingMethod')"
              >
                {{ partsStoreOrderDetailsDto.ShippingMethod }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <span v-else>{{ partsStoreOrderDetailsDto.ShippingMethod }}</span>
            </template>
            <template slot="FreightTerms">
              <b-form-group v-show="isOpen && showEditing.FreightTerm">
                <b-form-select
                  id="freightTerms"
                  v-model="editObject.FreightTerm"
                  class="disableClear"
                  :options="freightTermOptions"
                />
                <span
                  v-show="showEditing.FreightTerm"
                  @click="save(); toggleShowEditing('FreightTerm', true);"
                >
                  <i
                    v-show="isOpen"
                    class="icon-checkmark icon-medium ml-1 mr-1"
                  />
                </span>
                <span
                  v-show="showEditing.FreightTerm"
                  @click="toggleShowEditing('FreightTerm')"
                >
                  <i v-show="isOpen" class="icon-x icon-medium" />
                </span>
              </b-form-group>
              <span
                v-if="!showEditing.FreightTerm && !loadingOrderData"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('FreightTerm')"
              >
                {{ getFreightTermText(partsStoreOrderDetailsDto.FreightTerm) }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <span
                v-else-if="!loadingOrderData"
              >{{ getFreightTermText(partsStoreOrderDetailsDto.FreightTerm) }}</span>
            </template>
            <template
              slot="Date"
            >
              <span v-if="!loadingOrderData">
                {{ getLocalDateTime(partsStoreOrderDetailsDto.CreatedDate, false, true) }}
              </span>
            </template>
            <template slot="Status">
              {{ getStatusText(partsStoreOrderDetailsDto.Status) }}
              <span
                v-if="isClosed && partsStoreOrderDetailsDto.OrderStatusReasonId !== null"
              >- {{ getOrderStatusReasonText(partsStoreOrderDetailsDto.OrderStatusReasonId) }}</span>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row>
        <b-col v-if="isOpen" xs="12" lg="6">
          <h6>{{ getContentItem("billTo") }}:</h6>
          <br>
          <b-row>
            <b-col xs="4">
              <label for="BillingName">
                <b>{{ getContentItem("name") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingName"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingName')"
              >
                {{ !isNullOrWhiteSpace(partsStoreOrderDetailsDto.BillingName) ? partsStoreOrderDetailsDto.BillingName : "Add Billing Name..." }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.BillingName"
                ref="BillingName"
                v-model="editObject.BillingName"
                type="text"
                name="BillingName"
              >
              <span
                v-show="showEditing.BillingName"
                @click="save(); toggleShowEditing('BillingName', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingName"
                @click="toggleShowEditing('BillingName')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="BillingAddressLine1">
                <b>{{ getContentItem("addressLine1") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingAddressLine1"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingAddressLine1')"
              >
                {{ partsStoreOrderDetailsDto.BillingAddressLine1 }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.BillingAddressLine1"
                ref="BillingAddressLine1"
                v-model="editObject.BillingAddressLine1"
                type="text"
                name="BillingAddressLine1"
              >
              <span
                v-show="showEditing.BillingAddressLine1"
                @click="save(); toggleShowEditing('BillingAddressLine1', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingAddressLine1"
                @click="toggleShowEditing('BillingAddressLine1')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <!-- Let's only show optional address lines if they exist -->
          <b-row v-if="partsStoreOrderDetailsDto.BillingAddressLine2">
            <b-col xs="4">
              <label for="BillingAddressLine2">
                <b>{{ getContentItem("addressLine2") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingAddressLine2"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingAddressLine2')"
              >
                {{ partsStoreOrderDetailsDto.BillingAddressLine2 }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.BillingAddressLine2"
                ref="BillingAddressLine2"
                v-model="editObject.BillingAddressLine2"
                type="text"
                name="BillingAddressLine2"
              >
              <span
                v-show="showEditing.BillingAddressLine2"
                @click="save(); toggleShowEditing('BillingAddressLine2', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingAddressLine2"
                @click="toggleShowEditing('BillingAddressLine2')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row v-if="partsStoreOrderDetailsDto.BillingAddressLine3">
            <b-col xs="4">
              <label for="BillingAddressLine3">
                <b>{{ getContentItem("addressLine3") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingAddressLine3"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingAddressLine3')"
              >
                {{ partsStoreOrderDetailsDto.BillingAddressLine3 }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.BillingAddressLine3"
                ref="BillingAddressLine3"
                v-model="editObject.BillingAddressLine3"
                type="text"
                name="BillingAddressLine3"
              >
              <span
                v-show="showEditing.BillingAddressLine3"
                @click="save(); toggleShowEditing('BillingAddressLine3', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingAddressLine3"
                @click="toggleShowEditing('BillingAddressLine3')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="BillingCity">
                <b>{{ getContentItem("city") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingCity"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingCity')"
              >
                {{ partsStoreOrderDetailsDto.BillingCity }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.BillingCity"
                ref="BillingCity"
                v-model="editObject.BillingCity"
                type="text"
                name="BillingCity"
              >
              <span
                v-show="showEditing.BillingCity"
                @click="save(); toggleShowEditing('BillingCity', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingCity"
                @click="toggleShowEditing('BillingCity')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="BillingState">
                <b>{{ getContentItem("addressState") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingState"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingState')"
              >
                {{ partsStoreOrderDetailsDto.BillingState }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.BillingState"
                ref="BillingState"
                v-model="editObject.BillingState"
                type="text"
                name="BillingState"
              >
              <span
                v-show="showEditing.BillingState"
                @click="save(); toggleShowEditing('BillingState', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingState"
                @click="toggleShowEditing('BillingState')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="BillingPostalCode">
                <b>{{ getContentItem("postalcode") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.BillingPostalCode"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('BillingPostalCode')"
              >
                {{ partsStoreOrderDetailsDto.BillingPostalCode }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.BillingPostalCode"
                ref="BillingPostalCode"
                v-model="editObject.BillingPostalCode"
                type="text"
                name="BillingPostalCode"
              >
              <span
                v-show="showEditing.BillingPostalCode"
                @click="save(); toggleShowEditing('BillingPostalCode', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.BillingPostalCode"
                @click="toggleShowEditing('BillingPostalCode')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>
          <br>
        </b-col>
        <b-col v-else xs="12" lg="6">
          <h6>{{ getContentItem("billTo") }}:</h6>
          <b-row>
            <b-col xs="12">
              {{ partsStoreOrderDetailsDto.BillingName }}
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              {{ partsStoreOrderDetailsDto.BillingAddressLine1 }}
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              {{ partsStoreOrderDetailsDto.BillingAddressLine2 }}
            </b-col>
          </b-row>
          <b-row>
            <b-col
              xs="12"
            >
              {{ partsStoreOrderDetailsDto.BillingCity }}, {{ partsStoreOrderDetailsDto.BillingState }} {{ partsStoreOrderDetailsDto.BillingPostalCode }}
            </b-col>
          </b-row>
          <br>
        </b-col>
        <b-col v-if="isOpen" xs="12" lg="6">
          <h6>{{ getContentItem("shipTo") }}:</h6>
          <br>

          <b-row>
            <b-col xs="4">
              <label for="ShippingAddressLine1">
                <b>{{ getContentItem("addressLine1") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ShippingAddressLine1"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingAddressLine1')"
              >
                {{ partsStoreOrderDetailsDto.ShippingAddressLine1 }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.ShippingAddressLine1"
                ref="ShippingAddressLine1"
                v-model="editObject.ShippingAddressLine1"
                type="text"
                name="ShippingAddressLine1"
              >
              <span
                v-show="showEditing.ShippingAddressLine1"
                @click="save(); toggleShowEditing('ShippingAddressLine1', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ShippingAddressLine1"
                @click="toggleShowEditing('ShippingAddressLine1')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <!-- Let's only show optional address lines if they exist -->
          <b-row v-if="partsStoreOrderDetailsDto.ShippingAddressLine2">
            <b-col xs="4">
              <label for="ShippingAddressLine2">
                <b>{{ getContentItem("addressLine2") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ShippingAddressLine2"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingAddressLine2')"
              >
                {{ partsStoreOrderDetailsDto.ShippingAddressLine2 }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.ShippingAddressLine2"
                ref="ShippingAddressLine2"
                v-model="editObject.ShippingAddressLine2"
                type="text"
                name="ShippingAddressLine2"
              >
              <span
                v-show="showEditing.ShippingAddressLine2"
                @click="save(); toggleShowEditing('ShippingAddressLine2', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ShippingAddressLine2"
                @click="toggleShowEditing('ShippingAddressLine2')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row v-if="partsStoreOrderDetailsDto.ShippingAddressLine3">
            <b-col xs="4">
              <label for="ShippingAddressLine3">
                <b>{{ getContentItem("addressLine3") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ShippingAddressLine2"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingAddressLine3')"
              >
                {{ partsStoreOrderDetailsDto.ShippingAddressLine3 }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.ShippingAddressLine3"
                ref="ShippingAddressLine3"
                v-model="editObject.ShippingAddressLine3"
                type="text"
                name="ShippingAddressLine3"
              >
              <span
                v-show="showEditing.ShippingAddressLine3"
                @click="save(); toggleShowEditing('ShippingAddressLine3', true );"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ShippingAddressLine3"
                @click="toggleShowEditing('ShippingAddressLine3')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="ShippingCity">
                <b>{{ getContentItem("city") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ShippingCity"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingCity')"
              >
                {{ partsStoreOrderDetailsDto.ShippingCity }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.ShippingCity"
                ref="ShippingCity"
                v-model="editObject.ShippingCity"
                type="text"
                name="ShippingCity"
              >
              <span
                v-show="showEditing.ShippingCity"
                @click="save(); toggleShowEditing('ShippingCity', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ShippingCity"
                @click="toggleShowEditing('ShippingCity')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="ShippingState">
                <b>{{ getContentItem("addressState") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ShippingState"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingState')"
              >
                {{ partsStoreOrderDetailsDto.ShippingState }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.ShippingState"
                ref="ShippingState"
                v-model="editObject.ShippingState"
                type="text"
                name="ShippingState"
              >
              <span
                v-show="showEditing.ShippingState"
                @click="save(); toggleShowEditing('ShippingState', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ShippingState"
                @click="toggleShowEditing('ShippingState')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col xs="4">
              <label for="ShippingPostalCode">
                <b>{{ getContentItem("postalcode") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ShippingPostalCode"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ShippingPostalCode')"
              >
                {{ partsStoreOrderDetailsDto.ShippingPostalCode }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <input
                v-show="showEditing.ShippingPostalCode"
                ref="ShippingPostalCode"
                v-model="editObject.ShippingPostalCode"
                type="text"
                name="ShippingPostalCode"
              >
              <span
                v-show="showEditing.ShippingPostalCode"
                @click="save(); toggleShowEditing('ShippingPostalCode', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ShippingPostalCode"
                @click="toggleShowEditing('ShippingPostalCode')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>
          <br>
        </b-col>
        <b-col v-else xs="12" lg="6">
          <h6>{{ getContentItem("shipTo") }}:</h6>
          <b-row>
            <b-col xs="12">
              {{ partsStoreOrderDetailsDto.ShippingAddressLine1 }}
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              {{ partsStoreOrderDetailsDto.ShippingAddressLine2 }}
            </b-col>
          </b-row>
          <b-row>
            <b-col
              xs="12"
            >
              {{ partsStoreOrderDetailsDto.ShippingCity }}, {{ partsStoreOrderDetailsDto.ShippingState }} {{ partsStoreOrderDetailsDto.ShippingPostalCode }}
            </b-col>
          </b-row>
          <br>
        </b-col>
      </b-row>

      <b-row>
        <b-col xs="12" lg="6">
          <b-row>
            <b-col xs="4">
              <label for="ThirdPartyBillingAccountNumber">
                <b>{{ getContentItem("thirdPartBillingAccount") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.ThirdPartyBillingAccountNumber && isOpen"
                :class="{editable: isOpen}"
                @click="toggleShowEditing('ThirdPartyBillingAccountNumber')"
              >
                {{ !isNullOrWhiteSpace(partsStoreOrderDetailsDto.ThirdPartyBillingAccountNumber) ? partsStoreOrderDetailsDto.ThirdPartyBillingAccountNumber : this.labels.addThirdPartBillingDotDotDot }}
                <i v-show="isOpen" class="icon-pencil" />
              </span>
              <span
                v-if="isClosed"
              >{{ partsStoreOrderDetailsDto.ThirdPartyBillingAccountNumber }}</span>
              <input
                v-show="showEditing.ThirdPartyBillingAccountNumber"
                ref="ThirdPartyBillingAccountNumber"
                v-model="editObject.ThirdPartyBillingAccountNumber"
                type="text"
                name="ThirdPartyBillingAccountNumber"
              >
              <span
                v-show="showEditing.ThirdPartyBillingAccountNumber"
                @click="save(); toggleShowEditing('ThirdPartyBillingAccountNumber', true);"
              >
                <i v-show="isOpen" class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.ThirdPartyBillingAccountNumber"
                @click="toggleShowEditing('ThirdPartyBillingAccountNumber')"
              >
                <i v-show="isOpen" class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>
        </b-col>
        <b-col xs="12" lg="6">
          <b-row>
            <b-col xs="4">
              <label for="TrackingNumber">
                <b>{{ getContentItem("trackingInformation") }}:</b>
              </label>
            </b-col>
            <b-col xs="8">
              <span
                v-if="!showEditing.TrackingNumber"
                class="editable"
                @click="toggleShowEditing('TrackingNumber')"
              >
                {{ !isNullOrWhiteSpace(partsStoreOrderDetailsDto.TrackingNumber) ? partsStoreOrderDetailsDto.TrackingNumber : this.labels.addTrackingInformationDotDotDot }}
                <i
                  v-show="isOpen"
                  class="icon-pencil"
                />
              </span>
              <input
                v-show="showEditing.TrackingNumber"
                ref="TrackingNumber"
                v-model="editObject.TrackingNumber"
                type="text"
                name="TrackingNumber"
              >
              <span
                v-show="showEditing.TrackingNumber"
                @click="save(); toggleShowEditing('TrackingNumber', true);"
              >
                <i class="icon-checkmark icon-medium mr-1" />
              </span>
              <span
                v-show="showEditing.TrackingNumber"
                @click="toggleShowEditing('TrackingNumber')"
              >
                <i class="icon-x icon-medium" />
              </span>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <br>
      <h2>{{ getContentItem("fulfillmentOptions") }}</h2>
      <b-row v-if="isOpen" class="noprint">
        <b-col xs="12">
          <span class="pull-right">
            {{ getContentItem("fulfillAllvia") }}:
            <span
              class="noHrefLink"
              @click="updateFulfillmentOnAllItems(1)"
            >{{ getContentItem("dealerNet") }}</span> |
            <span
              class="noHrefLink"
              @click="updateFulfillmentOnAllItems(2)"
            >{{ getContentItem("inventory") }}</span>
          </span>
        </b-col>
      </b-row>

      <b-row>
        <b-col xs="12">
          <b-table
            id="orderItemsTable"
            striped
            hover
            stacked="md"
            class="blue"
            show-empty
            :fields="orderItemsColumns"
            :items="partsStoreOrderDetailsDto.Items"
            :no-provider-filtering="true"
            :no-local-sorting="true"
          >
            <template slot="FulfillmentType" slot-scope="row">
              <b-form-group v-if="isOpen" class="parts-store-order-detail__fullfillment-select">
                <b-form-select
                  v-model="row.item.FulfillmentType"
                  class="disableClear"
                  :options="fulfillmentOptions"
                  @change="updateFulfillment(row.item.Id, row.item.FulfillmentType); showSplitOptionsIndex = row.index"
                />
                <div v-if="row.index === showSplitOptionsIndex" class="parts-store-order-details__split-fulfilment-form">
                  <h3>Split Item</h3>
                  <label>{{ getContentItem("dealerNetQuantity") }}</label>
                  <b-form-input v-model="splitOrderItem.DealerNetQuantity" type="number" min="1" />
                  <br>
                  <label>{{ getContentItem("inventoryQuantity") }}</label>
                  <b-form-input v-model="splitOrderItem.InventoryQuantity" type="number" min="1" />
                  <button class="btn dn-btn-large btn-large" @click="splitItem(splitOrderItem.OrderItemId, splitOrderItem.DealerNetQuantity, splitOrderItem.InventoryQuantity)">
                    <span v-if="!processingOrder">Split</span>
                  </button>
                  <button class="btn btn-large" @click="showSplitOptionsIndex = -1">
                    <span v-if="!processingOrder">Cancel</span>
                  </button>
                </div>
              </b-form-group>
              <span v-else>{{ getFulfillmentText(row.item.FulfillmentType) }}</span>
            </template>
            <template slot="McfPartNumber" slot-scope="row">
              <span class="price">{{ row.item.McfPartNumber }}</span>
            </template>
            <template slot="Fullfillment" slot-scope="row">
              <b-form-group>
                <label>{{ getContentItem("dealerNetQuantity") }}</label>
                <b-form-input v-model="row.DealerNetQuantity" type="number" min="1" />
                <br>
                <label>{{ getContentItem("inventoryQuantity") }}</label>
                <b-form-input v-model="row.InventoryQuantity" type="number" min="1" />
              </b-form-group>
            </template>
            <template
              slot="PartDescription"
              slot-scope="row"
            >
              {{ row.item.PartDescription }}
            </template>
            <template slot="Stock" slot-scope="row">
              <StockInformationPopover
                :part-id="row.item.PartId"
                :account-customer-part-id="row.item.AccountCustomerPartId"
                :order-item-id="row.item.Id"
              />
            </template>
            <template slot="Quantity" slot-scope="row">
              <input
                v-if="isOpen"
                v-model="row.item.Quantity"
                name="quantity"
                type="number"
                class="quantity-input"
                min="0"
                @change="updateOrderItemQuantity(row.item.Id, row.item.Quantity)"
              >
              <span v-else>{{ row.item.Quantity }}</span>
            </template>
            <template slot="DealerNetPrice" slot-scope="row">
              <currency-text
                :amount="DealerNetPrice(row.item.PartId, 1 )"
                :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
              />
              <p v-if="(row.item.CoreCostTargetCurrency > 0)">
                <i>{{ getContentItem("coreCost") }}: &nbsp;</i>
                <currency-text
                  :amount="row.item.CoreCostTargetCurrency"
                  :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
                />
              </p>
            </template>
            <template slot="DealerNetTotal" slot-scope="row">
              <currency-text
                :amount="DealerNetPrice(row.item.PartId, row.item.Quantity )"
                :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
              />
              <p v-if="(row.item.CoreCostTargetCurrency > 0)">
                <currency-text
                  :amount="row.item.CoreCostTargetCurrency * row.item.Quantity"
                  :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
                />
              </p>
            </template>
            <template slot="ExtendedPriceTargetCurrency" slot-scope="row">
              <currency-text
                :amount="row.item.ExtendedPriceTargetCurrency"
                :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
              />
              <p v-if="(row.item.CoreCostTargetCurrency > 0)">
                <currency-text
                  :amount="row.item.CoreCostTargetCurrency"
                  :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
                />
              </p>
            </template>
            <template slot="TotalTargetCurrency" slot-scope="row">
              <currency-text
                :amount="row.item.TotalTargetCurrency"
                :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
              />
              <p v-if="(row.item.CoreCostTargetCurrency > 0)">
                <currency-text
                  :amount="row.item.CoreCostTargetCurrency * row.item.Quantity"
                  :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
                />
              </p>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row>
        <b-col offset="9" xs="1">
          <b class="pull-right">{{ getContentItem("subtotal") }}:</b>
        </b-col>
        <b-col xs="2">
          <b class="pull-right">
            <currency-text
              :amount="subtotal"
              :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
            />
          </b>
        </b-col>
      </b-row>
      <b-row>
        <b-col offset="9" xs="1">
          <b class="pull-right">{{ getContentItem("salesTax") }}:</b>
        </b-col>
        <b-col xs="2">
          <b class="pull-right">
            <currency-text
              :amount="partsStoreOrderDetailsDto.SalesTaxTargetCurrency"
              :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
            />
          </b>
        </b-col>
      </b-row>
      <b-row>
        <b-col offset="9" xs="1">
          <b class="pull-right">{{ getContentItem("orderTotal") }}:</b>
        </b-col>
        <b-col xs="2">
          <b class="pull-right">
            <currency-text
              :amount="partsStoreOrderDetailsDto.OrderTotalTargetCurrency"
              :currency-code="partsStoreOrderDetailsDto.TargetCurrency"
            />
          </b>
        </b-col>
      </b-row>
      <br>
      <b-row v-if="isOpen">
        <b-col xs="12">
          <div class="pull-right noprint">
            <button class="btn dn-btn-large btn-large" :disabled="processingOrder" @click="ProcessOrder()">
              <span v-if="!processingOrder">{{ getContentItem("processOrder") }}</span>
              <LoadingSpinner v-if="processingOrder" />
            </button>
          </div>
        </b-col>
      </b-row>
      <div class="parts-store-order-detail__footer-div">
        <div v-if="isOpen" class="parts-store-order-detail__cancel-div">
          <h3>Cancellation Reason</h3>
          <b-form-select
            id="cancellationReason"
            v-model="editObject.OrderStatusReasonId"
            class="disableClear"
            :options="cancellationReasonOptions"
          >
            <template slot="first">
              <option
                :value="null"
                disabled
              >
                -- {{ getContentItem("selectCancellationReason") }} --
              </option>
            </template>
          </b-form-select>
          <b-button
            class="btn btn-inverse btn-large"
            @click="editObject.Status = 5; save();"
          >
            {{ getContentItem("cancelOrder") }}
          </b-button>
        </div>
        <button class="btn dn-btn-large btn-large" @click="showHistory = !showHistory">
          {{ statusHistoryButtonText }}
        </button>
        <b-table
          v-if="showHistory"
          id="orderStatusHistory"
          striped
          hover
          stacked="md"
          class="blue"
          show-empty
          :fields="orderStatusHistoryColumns"
          :items="orderStatusHistory"
          :no-provider-filtering="true"
          :no-local-sorting="true"
        >
          <template slot="Name" slot-scope="row" class="parts-store-order-details__history-table-header">
            {{ row.item.ModifiedBy.FirstName }} {{ row.item.ModifiedBy.LastName }}
            <br>
            {{ row.item.ModifiedBy.EmailAddress }}
          </template>
          <template slot="Status" slot-scope="row">
            {{ row.item.Notes }}
          </template>
          <template
            slot="Date"
            slot-scope="row"
          >
            {{ getLocalDateTime(row.item.ModifiedDate, false, true) }}
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
  import { sessionHelper } from "@/components/mixins/sessionHelper";
  import { stringHelper } from "@/components/mixins/stringHelper";
  import { formatDateTime } from "@/components/mixins/formatDateTime";
  import CurrencyText from "@/components/Elements/CurrencyText";
  import LoadingSpinner from "@/components/Elements/LoadingSpinner";
  import StockInformationPopover from "@/components/Elements/StockInformationPopover";
  import OrderDetailsLink from "@/components/Elements/OrderDetailsLink";
  import {partsStoreOrderDetailsMixin} from "@/components/mixins/partsStoreOrderDetailsMixin"

  export default {
    components: {
      CurrencyText,
      LoadingSpinner,
      StockInformationPopover,
      OrderDetailsLink,
    },
    mixins: [stringHelper, formatDateTime, sessionHelper, partsStoreOrderDetailsMixin],
    props: {
      orderId: {
        type: String,
        default: null,
      },
      customerId: {
        type: String,
        default: null,
      },
      poNumber: {
        type: String,
        default: null,
      }
    },
    created() {
        this.orderInfoColumns[0].label = this.getContentItem("customer");
        this.orderInfoColumns[1].label = this.getContentItem("customerPO");
        this.orderInfoColumns[2].label = this.getContentItem("shipVia");
        this.orderInfoColumns[3].label = this.getContentItem("freightTerms");
        this.orderInfoColumns[4].label = this.getContentItem("date");
        this.orderInfoColumns[5].label = this.getContentItem("status");

        this.orderItemsColumns[0].label = this.getContentItem("fulfillment");
        this.orderItemsColumns[1].label = this.getContentItem("part");
        this.orderItemsColumns[2].label = this.getContentItem("description");
        this.orderItemsColumns[3].label = this.getContentItem("stock");
        this.orderItemsColumns[4].label = this.getContentItem("qty");
        this.orderItemsColumns[5].label = this.getContentItem("dealerNetPrice");
        this.orderItemsColumns[6].label = this.getContentItem("dealerNetTotal");
        this.orderItemsColumns[7].label = this.getContentItem("customerPrice");
        this.orderItemsColumns[8].label = this.getContentItem("customerTotal");

        this.orderStatusHistoryColumns[0].label = this.getContentItem("name");
        this.orderStatusHistoryColumns[1].label = this.getContentItem("status");
        this.orderStatusHistoryColumns[2].label = this.getContentItem("date");
        this.labels = {
          statusHistory: this.getContentItem("statusHistory"),
          cancelOrder: this.getContentItem("cancelOrder"),
          dismiss: this.getContentItem("dismiss"),
          addThirdPartBillingDotDotDot: this.getContentItem("addThirdPartBillingDotDotDot"),
          addTrackingInformationDotDotDot: this.getContentItem("addTrackingInformationDotDotDot"),
        };
      },
      mounted() {
        if (!this.orderId && this.poNumber && this.customerId) {
          this.GetSmartEquipOrder(); // check for SmartEquip Order, and set the dataOrderId
        } else {
          this.dataOrderId = this.orderId;
          this.GetOrderDetailsDto();
        }
      }
  }

</script>
<style>
.quantity-input {
    width: 100% !important;
    min-width: 40px;
}

.price {
    text-align: right;
    white-space: nowrap;
}
.editable:hover,
.editable:hover > .icon-pencil {
    background-color: #e6e6e6;
    display: inline-block !important;
    white-space: nowrap;
}

.editable > .icon-pencil {
    display: none;
}

.hidden {
    display: none !important;
}

.icon-checkmark:hover,
.icon-x:hover {
    background-color: #e6e6e6;
}

.icon-medium {
    transform: scale(1.4);
}

div > label {
    margin-bottom: 0px;
}

.parts-store-order-detail__footer-div{
  border-top:1px solid black;
  margin-top: 10px;
}
.parts-store-order-detail__footer-div *{
   margin-top: 10px;
}
.parts-store-order-details__history-table-header{
  width:30%;
}
.parts-store-order-details__split-fulfilment-form *{
  margin-top: 5px;
}
.parts-store-order-detail__fullfillment-select{
  width: 200px;
}
</style>
